import { useLocalStorage } from '@vueuse/core';
import { AuthVia, AuthViaStorageKey } from '~/enums/auth.enum';

export type AuthContext = 'web' | 'app_webview_ios' | 'app_webview_android';

/**
 * Composable for detecting authentication context and platform.
 *
 * Uses local storage to reliably detect if in mobile app,
 * then uses user agent to determine iOS vs Android.
 */
export default function useAuthContext () {
  /**
   * Returns the current authentication context.
   * - 'app_webview_ios': User is in the iOS mobile app webview
   * - 'app_webview_android': User is in the Android mobile app webview
   * - 'web': User is in a regular web browser
   */
  const getAuthContext = (): AuthContext => {
    if (typeof window === 'undefined') {
      return 'web';
    }

    // Check local storage first (set via ?via=medmastery-app query param)
    const authVia = useLocalStorage(AuthViaStorageKey.AuthVia, null, {
      listenToStorageChanges: false,
    }).value;

    if (authVia === AuthVia.MedmasteryApp) {
      // Determine platform from user agent
      return getPlatform();
    }

    return 'web';
  };

  /**
   * Determines the platform (iOS or Android).
   * First checks local storage (passed from Flutter), then falls back to user agent.
   */
  const getPlatform = (): 'app_webview_ios' | 'app_webview_android' => {
    // Check local storage first (most reliable - passed from Flutter)
    const storedPlatform = useLocalStorage(AuthViaStorageKey.Platform, null, {
      listenToStorageChanges: false,
    }).value;

    if (storedPlatform === 'ios') {
      return 'app_webview_ios';
    }
    if (storedPlatform === 'android') {
      return 'app_webview_android';
    }

    // Fallback to user agent detection
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'app_webview_ios';
    }

    // Default to Android for non-iOS devices
    return 'app_webview_android';
  };

  /**
   * Checks if the current context is the mobile app webview.
   */
  const isMobileApp = (): boolean => {
    return getAuthContext() !== 'web';
  };

  /**
   * Checks if the current platform is iOS.
   */
  const isIOS = (): boolean => {
    return getAuthContext() === 'app_webview_ios';
  };

  /**
   * Checks if the current platform is Android.
   */
  const isAndroid = (): boolean => {
    return getAuthContext() === 'app_webview_android';
  };

  return {
    getAuthContext,
    isMobileApp,
    isIOS,
    isAndroid,
  };
}
